import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifySignaturePage = () => {
  const { token } = useParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);

  useEffect(() => {
    const verifySignature = async () => {
      try {
        const response = await axios.get(`${API}/verify-signature/${token}`);
        if (response.data.success) {
          setVerified(true);
          setRedirectUrl(response.data.redirect_url);
          setTimeout(() => {
            window.location.href = response.data.redirect_url;
          }, 3000);
        }
      } catch (err) {
        console.error('Signature verification failed:', err);
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifySignature();
    }
  }, [token]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Signature</h1>
          <p className="text-gray-600">Please wait while we verify your electronic signature...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-red-900 mb-2">Verification Failed</h1>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-gray-500 text-sm">Please contact support if this issue persists.</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-900 mb-2">Signature Verified!</h1>
          <p className="text-green-600 mb-4">Your electronic signature has been successfully verified and the contract is now legally binding.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 text-sm">Redirecting you to the checkout page in 3 seconds...</p>
          </div>
          {redirectUrl && (
            <Button 
              onClick={() => window.location.href = redirectUrl}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Continue to Checkout
            </Button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export const VerifySubscriptionSignaturePage = () => {
  const { token } = useParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verifySignature = async () => {
      try {
        const response = await axios.get(`${API}/verify-subscription-signature/${token}`);
        if (response.data.success) {
          setVerified(true);
          setSubscriptionData(response.data);
          setTimeout(() => {
            navigate(`/subscription/${response.data.subscription_slug}`);
          }, 3000);
        }
      } catch (err) {
        console.error('Subscription signature verification failed:', err);
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifySignature();
    }
  }, [token, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#e6007e] mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Verifying Signature</h1>
          <p className="text-gray-600 font-body">Please wait while we verify your electronic signature...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-red-900 mb-2 font-heading">Verification Failed</h1>
          <p className="text-red-600 mb-4 font-body">{error}</p>
          <p className="text-gray-500 text-sm font-body">Please contact the business owner if this issue persists.</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-900 mb-2 font-heading">Signature Verified!</h1>
          <p className="text-green-600 mb-4 font-body">
            Your signature for <strong>{subscriptionData?.subscription_title}</strong> has been verified.
          </p>
          <div className="bg-[#e6007e]/10 border border-[#e6007e]/20 rounded-lg p-4 mb-4">
            <p className="text-[#e6007e] text-sm font-body">Redirecting to payment setup in 3 seconds...</p>
          </div>
          <Button 
            onClick={() => navigate(`/subscription/${subscriptionData?.subscription_slug}`)}
            className="w-full bg-[#e6007e] hover:bg-[#c90068] text-white font-body"
          >
            Continue to Payment
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export const VerifyPaymentSignaturePage = () => {
  const { token } = useParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);

  useEffect(() => {
    const verifyPaymentSignature = async () => {
      try {
        const response = await axios.get(`${API}/verify-payment-signature/${token}`);
        if (response.data.success) {
          setVerified(true);
          setRedirectUrl(response.data.redirect_url);
          setTimeout(() => {
            window.location.href = response.data.redirect_url;
          }, 3000);
        }
      } catch (err) {
        console.error('Payment signature verification failed:', err);
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyPaymentSignature();
    }
  }, [token]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment Selection</h1>
          <p className="text-gray-600">Please wait while we verify your payment method selection and signature...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-red-900 mb-2">Verification Failed</h1>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-gray-500 text-sm">Please contact support@paify.io if this issue persists.</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-900 mb-2">Payment Method Confirmed!</h1>
          <p className="text-green-600 mb-4">Your payment method selection and signature have been verified. The contract is now active and legally binding.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 text-sm">Redirecting you to the payment portal in 3 seconds...</p>
          </div>
          {redirectUrl && (
            <Button 
              onClick={() => window.location.href = redirectUrl}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              Continue to Payment Portal
            </Button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export const TestModalPage = () => {
  const [showModal, setShowModal] = useState(false);
  const contractContent = `<div><h1>Test Contract</h1><p>This is a test contract to verify modal functionality works.</p><p>If you can see this, the modal system is working correctly.</p></div>`;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Modal Test Page</h1>
        <Button onClick={() => setShowModal(true)}>
          Open Test Modal
        </Button>
        
        {showModal && (
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Test Contract Modal</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <div 
                  dangerouslySetInnerHTML={{ __html: contractContent }}
                  className="contract-preview"
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};
